import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Add, Delete, ExpandMore } from '@mui/icons-material';
import { LayoutPreviewCanvas, type LayoutRect } from '../shared/layout-preview-canvas';
import { SLIDE_TEMPLATE_COMPONENT_IDS } from '../constants/component-ids';
import { PLAYER_TYPES } from '@/types/dtos/player/response/player.dto';
import type { SlideTemplateDto } from '@/state/queries/slide-templates/use-get-slide-templates';
import type { CreateSlideTemplateDto } from '@/state/mutations/slide-templates/use-create-slide-template';
import {
  isInfoOverlayComposite,
  useGetCompositeComponentsQuery,
} from '@/state/queries/composite-components/use-get-composite-components';
import { useGetHtmlComponentsQuery } from '@/state/queries/html-components/use-get-html-components';

interface TemplateLayer {
  order: number;
  componentId: string;
  position: { x: number; y: number; w: number; h: number; cutLeft?: number; cutRight?: number };
  dataBindings: Record<string, string>;
  style?: {
    backgroundColor?: string;
    textAlign?: string;
    maxLines?: number;
    textColor?: string;
    fontSizeMultiplier?: number;
  };
  /** Info-overlay composite slug; required when componentId is infoOverlay. */
  overlaySlug?: string;
  /** Raw JSON text while editing; not persisted. When set, displayed instead of stringified dataBindings. */
  dataBindingsRaw?: string;
}

interface SlideTemplateFormModalProps {
  open: boolean;
  onClose: () => void;
  template: SlideTemplateDto | null;
  onSave: (data: CreateSlideTemplateDto, templateId?: string) => Promise<void>;
  isSaving: boolean;
}

const defaultLayer = (order: number): TemplateLayer => ({
  order,
  componentId: 'background',
  position: { x: 0, y: 0, w: 100, h: 100 },
  dataBindings: {},
});

type LayerArea = { x: number; y: number; w: number; h: number };

interface CompositeHtmlRef {
  type?: string;
  ref?: string;
  position?: LayerArea;
}

function visibleLayerArea(layer: TemplateLayer): LayerArea {
  const { x, y, w, h, cutLeft = 0, cutRight = 0 } = layer.position;
  const totalCut = cutLeft + cutRight;
  if (totalCut <= 0 || totalCut >= 100) {
    return { x, y, w, h };
  }
  return {
    x: x + (w * cutLeft) / 100,
    y,
    w: (w * (100 - totalCut)) / 100,
    h,
  };
}

/** Map a composite child position (0–100) into a template layer area. */
function mapCompositePositionToArea(relative: LayerArea, area: LayerArea): LayerArea {
  return {
    x: area.x + (relative.x / 100) * area.w,
    y: area.y + (relative.y / 100) * area.h,
    w: (relative.w / 100) * area.w,
    h: (relative.h / 100) * area.h,
  };
}

function overlayHtmlPreviewRects(
  layer: TemplateLayer,
  overlays: { slug: string; definition?: { components?: unknown[] } }[],
  htmlNamesById: Map<string, string>,
): LayoutRect[] {
  if (layer.componentId !== 'infoOverlay' || !layer.overlaySlug) {
    return [];
  }
  const overlay = overlays.find((c) => c.slug === layer.overlaySlug);
  const children = (overlay?.definition?.components ?? []) as CompositeHtmlRef[];
  const fullArea: LayerArea = {
    x: layer.position.x,
    y: layer.position.y,
    w: layer.position.w,
    h: layer.position.h,
  };
  const visibleArea = visibleLayerArea(layer);
  return children.flatMap((c, i) => {
    if (c.type !== 'html' || !c.position) {
      return [];
    }
    const clipped = clipAreaToVisible(mapCompositePositionToArea(c.position, fullArea), visibleArea);
    if (!clipped) {
      return [];
    }
    return [
      {
        ...clipped,
        label: (c.ref && htmlNamesById.get(c.ref)) || `HTML ${i + 1}`,
      },
    ];
  });
}

/** Keep the part of `rect` that lies inside `visible`. A full miss is clipped away. */
function clipAreaToVisible(rect: LayerArea, visible: LayerArea): LayerArea | null {
  const x1 = Math.max(rect.x, visible.x);
  const y1 = Math.max(rect.y, visible.y);
  const x2 = Math.min(rect.x + rect.w, visible.x + visible.w);
  const y2 = Math.min(rect.y + rect.h, visible.y + visible.h);
  if (x2 <= x1 || y2 <= y1) {
    return null;
  }
  return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
}

export default function SlideTemplateFormModal({
  open,
  onClose,
  template,
  onSave,
  isSaving,
}: SlideTemplateFormModalProps) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [context, setContext] = useState<'event' | 'flow' | 'session' | 'slot'>('flow');
  const [type, setType] = useState<'shared' | 'organization' | 'event'>('shared');
  const [layers, setLayers] = useState<TemplateLayer[]>([defaultLayer(1)]);
  const [selectionCriteria, setSelectionCriteria] = useState<{
    playerTypes?: string[];
    orientation?: 'landscape' | 'portrait';
  }>({});
  const [previewRatio, setPreviewRatio] = useState<'16:9' | '9:16'>('16:9');
  const [dataBindingsErrors, setDataBindingsErrors] = useState<Record<number, string>>({});
  const { data: composites = [] } = useGetCompositeComponentsQuery();
  const { data: htmlComponents = [] } = useGetHtmlComponentsQuery();
  const infoOverlays = composites.filter((c) => isInfoOverlayComposite(c));
  const htmlNamesById = new Map(htmlComponents.map((h) => [h.id, h.name]));

  useEffect(() => {
    queueMicrotask(() => {
      if (template) {
        setName(template.name);
        setSlug(template.slug);
        setContext(template.context as 'event' | 'flow' | 'session' | 'slot');
        setType(template.type as 'shared' | 'organization' | 'event');
        const rawLayers = (template.templateJson?.layers as TemplateLayer[]) ?? [];
        setLayers(rawLayers.length > 0 ? rawLayers : [defaultLayer(1)]);
        const raw = (template.selectionCriteria as Record<string, unknown>) ?? {};
        // Normalize: support legacy playerType (string) and new playerTypes (array)
        const playerTypes = Array.isArray(raw.playerTypes)
          ? (raw.playerTypes as string[])
          : typeof raw.playerType === 'string' && raw.playerType
            ? [raw.playerType]
            : undefined;
        setSelectionCriteria({
          playerTypes,
          orientation: raw.orientation as 'landscape' | 'portrait' | undefined,
        });
      } else {
        setName('');
        setSlug('');
        setContext('flow');
        setType('shared');
        setLayers([defaultLayer(1)]);
        setSelectionCriteria({});
      }
      setDataBindingsErrors({});
    });
  }, [template, open]);

  const addLayer = () => {
    const nextOrder = Math.max(...layers.map((l) => l.order), 0) + 1;
    setLayers([...layers, defaultLayer(nextOrder)]);
    setDataBindingsErrors({});
  };

  const removeLayer = (index: number) => {
    if (layers.length <= 1) return;
    const next = layers.filter((_, i) => i !== index).map((l, i) => ({ ...l, order: i + 1 }));
    setLayers(next);
    setDataBindingsErrors((prev) => {
      const nextErrors: Record<number, string> = {};
      for (const [k, v] of Object.entries(prev)) {
        const ki = Number(k);
        if (ki < index) nextErrors[ki] = v;
        else if (ki > index) nextErrors[ki - 1] = v;
      }
      return nextErrors;
    });
  };

  const updateLayer = (index: number, updates: Partial<TemplateLayer>) => {
    const next = [...layers];
    next[index] = { ...next[index], ...updates };
    setLayers(next);
  };

  const updateLayerPosition = (index: number, field: 'x' | 'y' | 'w' | 'h' | 'cutLeft' | 'cutRight', value: number) => {
    const next = [...layers];
    const pos = { ...next[index].position, [field]: value };
    if (field === 'cutLeft' || field === 'cutRight') {
      if (value === 0) delete pos[field];
    }
    next[index] = { ...next[index], position: pos };
    setLayers(next);
  };

  const handleDataBindingsChange = (index: number, value: string) => {
    updateLayer(index, { dataBindingsRaw: value });
    setDataBindingsErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleDataBindingsBlur = (index: number) => {
    const layer = layers[index];
    const raw = layer.dataBindingsRaw ?? JSON.stringify(layer.dataBindings || {}, null, 2);
    const trimmed = raw.trim();
    if (!trimmed) {
      updateLayer(index, { dataBindings: {}, dataBindingsRaw: undefined });
      setDataBindingsErrors((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      return;
    }
    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        const normalized: Record<string, string> = {};
        for (const [k, v] of Object.entries(parsed)) {
          if (typeof v === 'string') normalized[k] = v;
        }
        updateLayer(index, { dataBindings: normalized, dataBindingsRaw: undefined });
        setDataBindingsErrors((prev) => {
          const next = { ...prev };
          delete next[index];
          return next;
        });
      } else {
        setDataBindingsErrors((prev) => ({ ...prev, [index]: 'Must be a JSON object' }));
      }
    } catch {
      setDataBindingsErrors((prev) => ({ ...prev, [index]: 'Invalid JSON' }));
    }
  };

  const previewRects: LayoutRect[] = layers.flatMap((l) => {
    const area = visibleLayerArea(l);
    const label = l.componentId === 'infoOverlay' && l.overlaySlug ? `overlay: ${l.overlaySlug}` : l.componentId;
    const childRects = overlayHtmlPreviewRects(l, infoOverlays, htmlNamesById);
    return [{ ...area, label }, ...childRects];
  });

  const handleSubmit = async () => {
    const data: CreateSlideTemplateDto = {
      name: name.trim(),
      slug: slug.trim(),
      context,
      type,
      templateJson: {
        version: 1,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omit dataBindingsRaw from payload
        layers: layers.map(({ dataBindingsRaw, overlaySlug, ...l }) => ({
          ...l,
          dataBindings: l.dataBindings || {},
          ...(l.componentId === 'infoOverlay' && overlaySlug ? { overlaySlug } : {}),
        })),
      },
      selectionCriteria:
        selectionCriteria.playerTypes?.length || selectionCriteria.orientation
          ? { playerTypes: selectionCriteria.playerTypes, orientation: selectionCriteria.orientation }
          : undefined,
    };
    await onSave(data, template?.id);
    onClose();
  };

  const trimmedSlug = slug.trim();
  const hasDataBindingsErrors = Object.keys(dataBindingsErrors).length > 0;
  const hasMissingOverlaySlug = layers.some((l) => l.componentId === 'infoOverlay' && !l.overlaySlug);
  const isValid =
    name.trim() && trimmedSlug && /^[a-z0-9-]+$/.test(trimmedSlug) && !hasDataBindingsErrors && !hasMissingOverlaySlug;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{template ? 'Edit Slide Template' : 'Create Slide Template'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
            <TextField
              label="Slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="my-template"
              fullWidth
              required
              disabled={!!template}
              helperText="Lowercase, hyphens only"
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Context</InputLabel>
              <Select value={context} label="Context" onChange={(e) => setContext(e.target.value as typeof context)}>
                <MenuItem value="event">event</MenuItem>
                <MenuItem value="flow">flow</MenuItem>
                <MenuItem value="session">session</MenuItem>
                <MenuItem value="slot">slot</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select value={type} label="Type" onChange={(e) => setType(e.target.value as typeof type)}>
                <MenuItem value="shared">shared</MenuItem>
                <MenuItem value="organization">organization</MenuItem>
                <MenuItem value="event">event</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2">Selection criteria (optional)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Stack spacing={2} direction="row" flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Player types</InputLabel>
                    <Select
                      multiple
                      value={selectionCriteria.playerTypes ?? []}
                      label="Player types"
                      onChange={(e) => {
                        const value = e.target.value;
                        const selected = typeof value === 'string' ? value.split(',') : value;
                        setSelectionCriteria({
                          ...selectionCriteria,
                          playerTypes: selected.length > 0 ? selected : undefined,
                        });
                      }}
                      renderValue={(selected) => (selected as string[]).join(', ')}
                    >
                      {PLAYER_TYPES.map((pt) => (
                        <MenuItem key={pt} value={pt}>
                          {pt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Orientation</InputLabel>
                    <Select
                      value={selectionCriteria.orientation ?? ''}
                      label="Orientation"
                      onChange={(e) =>
                        setSelectionCriteria({
                          ...selectionCriteria,
                          orientation: (e.target.value as 'landscape' | 'portrait') || undefined,
                        })
                      }
                    >
                      <MenuItem value="">Any</MenuItem>
                      <MenuItem value="landscape">landscape</MenuItem>
                      <MenuItem value="portrait">portrait</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Capacity (read-only, derived from layers):
                  </Typography>
                  <Typography variant="body2" component="span">
                    Media:{' '}
                    {template
                      ? String((template.selectionCriteria as { mediaCapacity?: number })?.mediaCapacity ?? '—')
                      : 'Computed on save'}
                  </Typography>
                  <Typography variant="body2" component="span">
                    RoomCam:{' '}
                    {template
                      ? String((template.selectionCriteria as { roomCamCapacity?: number })?.roomCamCapacity ?? '—')
                      : 'Computed on save'}
                  </Typography>
                  <Typography variant="body2" component="span">
                    RemoteSpeaker:{' '}
                    {template
                      ? String(
                          (template.selectionCriteria as { remoteSpeakerCapacity?: number })?.remoteSpeakerCapacity ??
                            '—',
                        )
                      : 'Computed on save'}
                  </Typography>
                  <Typography variant="body2" component="span">
                    Translation:{' '}
                    {template
                      ? String(
                          (template.selectionCriteria as { liveTranslationCapacity?: number })
                            ?.liveTranslationCapacity ?? '—',
                        )
                      : String(layers.filter((l) => l.componentId === 'liveTranslation').length)}
                  </Typography>
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">Layers</Typography>
              <Button size="small" startIcon={<Add />} onClick={addLayer}>
                Add layer
              </Button>
            </Box>
            <Stack spacing={1} sx={{ maxHeight: 280, overflow: 'auto' }}>
              {layers.map((layer, i) => (
                <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }} key={i}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Component</InputLabel>
                      <Select
                        value={layer.componentId}
                        label="Component"
                        onChange={(e) => updateLayer(i, { componentId: e.target.value })}
                      >
                        {SLIDE_TEMPLATE_COMPONENT_IDS.map((id) => (
                          <MenuItem key={id} value={id}>
                            {id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {layer.componentId === 'infoOverlay' ? (
                      <FormControl size="small" sx={{ minWidth: 180 }} error={!layer.overlaySlug}>
                        <InputLabel>Info overlay</InputLabel>
                        <Select
                          value={layer.overlaySlug ?? ''}
                          label="Info overlay"
                          onChange={(e) => updateLayer(i, { overlaySlug: e.target.value || undefined })}
                        >
                          <MenuItem value="">Select...</MenuItem>
                          {layer.overlaySlug && !infoOverlays.some((c) => c.slug === layer.overlaySlug) ? (
                            <MenuItem value={layer.overlaySlug}>{layer.overlaySlug} (missing)</MenuItem>
                          ) : null}
                          {infoOverlays.map((c) => (
                            <MenuItem key={c.id} value={c.slug}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : null}
                    <TextField
                      label="X"
                      type="number"
                      size="small"
                      value={layer.position.x}
                      onChange={(e) => updateLayerPosition(i, 'x', parseFloat(e.target.value) || 0)}
                      sx={{ width: 60 }}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                    />
                    <TextField
                      label="Y"
                      type="number"
                      size="small"
                      value={layer.position.y}
                      onChange={(e) => updateLayerPosition(i, 'y', parseFloat(e.target.value) || 0)}
                      sx={{ width: 60 }}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                    />
                    <TextField
                      label="W"
                      type="number"
                      size="small"
                      value={layer.position.w}
                      onChange={(e) => updateLayerPosition(i, 'w', parseFloat(e.target.value) || 0)}
                      sx={{ width: 60 }}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                    />
                    <TextField
                      label="H"
                      type="number"
                      size="small"
                      value={layer.position.h}
                      onChange={(e) => updateLayerPosition(i, 'h', parseFloat(e.target.value) || 0)}
                      sx={{ width: 60 }}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                    />
                    <TextField
                      label="Cut L %"
                      type="number"
                      size="small"
                      value={layer.position.cutLeft ?? 0}
                      onChange={(e) => updateLayerPosition(i, 'cutLeft', parseFloat(e.target.value) || 0)}
                      sx={{ width: 70 }}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      placeholder="0"
                    />
                    <TextField
                      label="Cut R %"
                      type="number"
                      size="small"
                      value={layer.position.cutRight ?? 0}
                      onChange={(e) => updateLayerPosition(i, 'cutRight', parseFloat(e.target.value) || 0)}
                      sx={{ width: 70 }}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      placeholder="0"
                    />
                    <IconButton size="small" onClick={() => removeLayer(i)} color="error" disabled={layers.length <= 1}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                  <TextField
                    label="Data bindings (JSON)"
                    size="small"
                    fullWidth
                    sx={{ mt: 1 }}
                    value={layer.dataBindingsRaw ?? JSON.stringify(layer.dataBindings || {}, null, 2)}
                    onChange={(e) => handleDataBindingsChange(i, e.target.value)}
                    onBlur={() => handleDataBindingsBlur(i)}
                    placeholder='{"title": "event.title"}'
                    error={!!dataBindingsErrors[i]}
                    helperText={dataBindingsErrors[i]}
                  />
                  {layer.componentId === 'liveTranslation' ? (
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                      <TextField
                        label="Text color"
                        size="small"
                        value={layer.style?.textColor ?? ''}
                        placeholder="Event default"
                        onChange={(e) =>
                          updateLayer(i, {
                            style: { ...layer.style, textColor: e.target.value || undefined },
                          })
                        }
                        sx={{ minWidth: 140 }}
                      />
                      <TextField
                        label="Background"
                        size="small"
                        value={layer.style?.backgroundColor ?? ''}
                        placeholder="rgba(0,0,0,0.6) or transparent"
                        onChange={(e) =>
                          updateLayer(i, {
                            style: { ...layer.style, backgroundColor: e.target.value || undefined },
                          })
                        }
                        sx={{ minWidth: 220 }}
                      />
                      <TextField
                        label="Max lines"
                        type="number"
                        size="small"
                        inputProps={{ min: 1, step: 1 }}
                        value={layer.style?.maxLines ?? 2}
                        onChange={(e) => {
                          const parsed = Number.parseInt(e.target.value, 10);
                          updateLayer(i, {
                            style: {
                              ...layer.style,
                              maxLines: Number.isFinite(parsed) && parsed > 0 ? parsed : 2,
                            },
                          });
                        }}
                        sx={{ width: 100 }}
                      />
                      <TextField
                        label="Font size"
                        type="number"
                        size="small"
                        inputProps={{ step: 0.1, min: 0.5 }}
                        value={layer.style?.fontSizeMultiplier ?? ''}
                        placeholder="Event default"
                        helperText="% of stage height"
                        onChange={(e) => {
                          const raw = e.target.value.trim();
                          const parsed = raw === '' ? undefined : parseFloat(raw);
                          updateLayer(i, {
                            style: {
                              ...layer.style,
                              fontSizeMultiplier:
                                parsed != null && Number.isFinite(parsed) && parsed > 0 ? parsed : undefined,
                            },
                          });
                        }}
                        sx={{ width: 140 }}
                      />
                    </Stack>
                  ) : null}
                </Box>
              ))}
            </Stack>
          </Box>

          <LayoutPreviewCanvas rects={previewRects} aspectRatio={previewRatio} onAspectRatioChange={setPreviewRatio} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!isValid || isSaving}>
          {template ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
